import React, { useRef, useState, useEffect, ReactNode } from "react";
import { Link } from "react-router-dom";
import NavBar from "../NavBar/NavBar.tsx";
import Footer from "../Footer/Footer.tsx";

/* Colours: text #4e5356 | buttons #2596be | headings #4573a6 | background white
   Fonts  : Ubuntu Bold (headings) / Open Sans Regular (body)
   Shared layout + building blocks for every blog post page (Blogpage1-6). */

const STYLES = `
@import url("https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600&family=Ubuntu:wght@700&display=swap");

.bp{--text:#4e5356;--head:#4573a6;--btn:#2596be;--tint:#eef6fa;--line:#d3e4ed;
  position:relative;overflow:hidden;background:#fff;font-family:"Open Sans",sans-serif;font-weight:400;color:var(--text)}
.bp-wrap{max-width:1240px;margin:0 auto;padding:0 24px}

.bp-chip{display:inline-block;margin-bottom:16px;padding:6px 18px;border-radius:50px;background:var(--tint);border:1px solid var(--line);
  color:var(--head);font-size:12px;letter-spacing:2px;text-transform:uppercase}
.bp h1,.bp h2{margin:0 0 16px;font-family:"Ubuntu",sans-serif;font-weight:700;color:var(--head);line-height:1.15;cursor:default;transition:color .35s,letter-spacing .35s}
.bp h1{font-size:42px}
.bp h2{font-size:30px}
.bp h1:hover,.bp h2:hover{color:var(--btn);letter-spacing:.4px}
.bp h3{margin:26px 0 10px;font-family:"Ubuntu",sans-serif;font-weight:700;font-size:19px;color:var(--head)}
.bp-line{display:flex;align-items:center;gap:12px;margin-bottom:20px}
.bp-line i{display:block;height:2px;width:40px;border-radius:2px;background:var(--btn)}.bp-line i:last-child{background:var(--head)}
.bp p{margin:0 0 16px;font-size:15.5px;line-height:1.8;color:var(--text)}

.rv{opacity:0;transform:translateY(46px);transition:opacity .8s cubic-bezier(.2,.8,.2,1),transform .8s cubic-bezier(.2,.8,.2,1);transition-delay:var(--d,0s)}
.rv.in{opacity:1;transform:none}

/* buttons */
.bp-btn{position:relative;overflow:hidden;z-index:0;display:inline-flex;align-items:center;gap:8px;padding:11px 24px;border:none;border-radius:50px;
  background:var(--tint);color:var(--head);font-family:"Open Sans",sans-serif;font-weight:600;font-size:14px;cursor:pointer;text-decoration:none;
  box-shadow:0 0 0 1px var(--line) inset;transition:color .35s,box-shadow .35s}
.bp-btn::before{content:"";position:absolute;inset:0;z-index:-1;background:linear-gradient(90deg,var(--head),var(--btn));transform:translateX(-101%);transition:transform .45s cubic-bezier(.2,.8,.2,1)}
.bp-btn span{display:inline-block;transition:transform .35s}
.bp-btn:hover{color:#fff}
.bp-btn:hover::before{transform:none}
.bp-btn:hover span{transform:translateX(5px)}
.bp-btn.back:hover span{transform:translateX(-5px)}

/* ---------- HERO ---------- */
.bp-hero{display:flex;align-items:center;justify-content:space-between;gap:60px;padding:150px 0 60px}
.bp-text{flex:1;min-width:0;max-width:600px;animation:bpL .9s cubic-bezier(.2,.8,.2,1) backwards}
.bp-vis{position:relative;flex:none;width:46%;max-width:540px;animation:bpR .9s .15s cubic-bezier(.2,.8,.2,1) backwards}
.bp-ring{position:absolute;inset:-14px;border-radius:44px;overflow:hidden}
.bp-ring::before{content:"";position:absolute;left:50%;top:50%;width:200%;height:200%;margin:-100% 0 0 -100%;
  background:conic-gradient(from 0deg,#4573a6,#2596be,#a9d3e6,#4573a6);animation:bpSpin 8s linear infinite}
.bp-ring::after{content:"";position:absolute;inset:4px;border-radius:40px;background:#fff}
.bp-tilt{position:relative;overflow:hidden;border-radius:32px;background:#fff;border:1px solid var(--line);box-shadow:0 30px 50px -26px rgba(69,115,166,.5);
  transition:transform .15s ease-out;transform:perspective(900px) rotateX(0) rotateY(0)}
.bp-tilt img{display:block;width:100%;height:auto;max-height:380px;object-fit:cover}
.bp-meta{display:flex;flex-wrap:wrap;gap:10px;margin-bottom:18px}
.bp-pill{padding:7px 18px;border-radius:50px;background:var(--tint);border:1px solid var(--line);color:var(--head);font-size:13px;font-weight:600}
.bp-tags{display:flex;flex-wrap:wrap;gap:10px;margin-top:6px}
.bp-tag{position:relative;overflow:hidden;z-index:0;padding:7px 18px;border-radius:50px;background:var(--tint);border:1px solid var(--line);color:var(--head);
  font-size:14px;font-weight:600;cursor:default;transition:color .35s,border-color .35s,transform .35s cubic-bezier(.3,1.6,.5,1)}
.bp-tag::before{content:"";position:absolute;inset:0;z-index:-1;background:linear-gradient(90deg,var(--head),var(--btn));transform:translateY(101%);transition:transform .4s cubic-bezier(.2,.8,.2,1)}
.bp-tag:hover{color:#fff;border-color:transparent;transform:translateY(-3px)}
.bp-tag:hover::before{transform:none}

/* ---------- ARTICLE ---------- */
.bp-article{max-width:900px;margin:0 auto;padding:30px 0 90px}
.bp-intro{padding:22px 26px;margin-bottom:46px;border-radius:20px;background:var(--tint);border:1px solid var(--line);border-left:5px solid var(--btn)}
.bp-intro p{margin:0;font-size:17px}
.bp-sec{position:relative;margin-bottom:46px;padding:30px 32px;border-radius:24px;background:#fff;border:1px solid var(--line);
  box-shadow:0 14px 28px -22px rgba(69,115,166,.55);transition:box-shadow .4s,border-color .4s}
.bp-sec:hover{border-color:rgba(37,150,190,.45);box-shadow:0 30px 50px -26px rgba(37,150,190,.55)}
.bp-sec::after{content:"";display:block;clear:both}
.bp-sec-top{position:absolute;top:0;left:0;width:100%;height:5px;transform:scaleX(0);transform-origin:left;
  background:linear-gradient(90deg,var(--head),var(--btn));transition:transform .55s cubic-bezier(.2,.8,.2,1)}
.bp-sec:hover .bp-sec-top{transform:scaleX(1)}
.bp-sec>h2:first-of-type{font-size:26px}

.bp ul{margin:0 0 18px;padding:0;list-style:none}
.bp ul li{position:relative;margin-bottom:10px;padding-left:26px;font-size:15.5px;line-height:1.75}
.bp ul li::before{content:"";position:absolute;left:2px;top:.62em;width:10px;height:10px;border-radius:50%;background:linear-gradient(135deg,var(--head),var(--btn))}
.bp strong{color:var(--head);font-weight:600}

/* figure with caption */
.bp-fig{margin:6px 0 20px;overflow:hidden;border-radius:18px;background:#fff;border:1px solid var(--line);box-shadow:0 18px 30px -22px rgba(69,115,166,.55)}
.bp-fig.right{float:right;width:46%;margin-left:28px}
.bp-fig.left{float:left;width:46%;margin-right:28px}
.bp-fig.full{width:100%}
.bp-fig .img{overflow:hidden;background:var(--tint)}
.bp-fig img{display:block;width:100%;height:auto;transition:transform .5s cubic-bezier(.3,1.4,.5,1)}
.bp-fig:hover img{transform:scale(1.05)}
.bp-fig figcaption{padding:12px 16px;font-size:13px;font-style:italic;line-height:1.6;color:var(--text);background:var(--tint);border-top:1px solid var(--line)}

/* two column block (image + content) */
.bp-split{display:grid;grid-template-columns:1fr 1fr;gap:28px;align-items:start;margin-bottom:10px}
.bp-split.flip .bp-fig{order:2}
.bp-split .bp-fig{margin:0}

/* pull quote */
.bp-quote{margin:26px 0 8px;padding:20px 26px;border-radius:18px;background:var(--tint);border-left:5px solid var(--head);
  font-family:"Ubuntu",sans-serif;font-size:18px;line-height:1.6;color:var(--head)}

/* CTA */
.bp-cta{margin-top:10px;padding:40px 28px;text-align:center;border-radius:28px;background:linear-gradient(135deg,var(--tint),#fff);border:1px solid var(--line);
  box-shadow:0 30px 50px -30px rgba(69,115,166,.55)}
.bp-cta h2{font-size:26px}
.bp-cta p{max-width:520px;margin:0 auto 22px}

.bp-foot{display:flex;flex-wrap:wrap;gap:16px;align-items:center;justify-content:space-between;margin-top:40px;padding-top:24px;border-top:1px solid var(--line)}
.bp-foot small{font-size:12.5px;color:#8a9296}

@keyframes bpL{from{opacity:0;transform:translateX(-70px)}}
@keyframes bpR{from{opacity:0;transform:translateX(70px)}}
@keyframes bpSpin{to{transform:rotate(360deg)}}

@media(max-width:960px){
  .bp-hero{flex-direction:column;gap:64px;padding:120px 0 40px}
  .bp-text{max-width:100%}
  .bp-vis{width:100%;max-width:440px}
  .bp-split{grid-template-columns:1fr}
  .bp-split.flip .bp-fig{order:0}
}
@media(max-width:700px){
  .bp h1{font-size:30px}.bp h2,.bp-sec>h2:first-of-type{font-size:23px}
  .bp-sec{padding:22px 18px}
  .bp-fig.right,.bp-fig.left{float:none;width:100%;margin:0 0 20px}
}
@media(prefers-reduced-motion:reduce){
  .bp *,.bp *::before,.bp *::after{animation:none!important;transition:none!important}
  .rv{opacity:1;transform:none}
}
`;

/* fades a block in once it scrolls into view */
export const Reveal: React.FC<{ children: ReactNode; delay?: number; className?: string }> = ({
  children,
  delay = 0,
  className = "",
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShow(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={"rv " + className + (show ? " in" : "")}
      style={{ ["--d" as any]: delay + "s" }}
    >
      {children}
    </div>
  );
};

/* a card-style content section with an animated top bar */
export const Section: React.FC<{ title?: string; children: ReactNode; delay?: number }> = ({
  title,
  children,
  delay = 0,
}) => (
  <Reveal delay={delay}>
    <section className="bp-sec">
      <div className="bp-sec-top" />
      {title && <h2>{title}</h2>}
      {children}
    </section>
  </Reveal>
);

/* image + caption. side: right | left | full */
export const Figure: React.FC<{
  src: string;
  alt: string;
  caption?: string;
  side?: "right" | "left" | "full";
}> = ({ src, alt, caption, side = "full" }) => (
  <figure className={"bp-fig " + side}>
    <div className="img">
      <img src={src} alt={alt} loading="lazy" />
    </div>
    {caption && <figcaption>{caption}</figcaption>}
  </figure>
);

/* image on one side, content on the other */
export const Split: React.FC<{
  src: string;
  alt: string;
  caption?: string;
  flip?: boolean;
  children: ReactNode;
}> = ({ src, alt, caption, flip = false, children }) => (
  <div className={"bp-split" + (flip ? " flip" : "")}>
    <Figure src={src} alt={alt} caption={caption} side="full" />
    <div>{children}</div>
  </div>
);

export const Quote: React.FC<{ children: ReactNode }> = ({ children }) => (
  <div className="bp-quote">{children}</div>
);

export const Cta: React.FC<{ title: string; text: string; label: string; to?: string }> = ({
  title,
  text,
  label,
  to = "/contact",
}) => (
  <Reveal>
    <div className="bp-cta">
      <h2>{title}</h2>
      <p>{text}</p>
      <Link to={to} className="bp-btn">
        {label} <span>→</span>
      </Link>
    </div>
  </Reveal>
);

type LayoutProps = {
  title: string;
  date: string;
  readTime?: string;
  heroImg: string;
  heroAlt: string;
  tags?: string[];
  intro?: string;
  disclaimer?: string;
  backTo?: string;
  children: ReactNode;
};

const BlogPostLayout: React.FC<LayoutProps> = ({
  title,
  date,
  readTime = "5 min read",
  heroImg,
  heroAlt,
  tags = [],
  intro,
  disclaimer,
  backTo = "/Blog",
  children,
}) => {
  const tiltRef = useRef<HTMLDivElement>(null);

  // gentle 3D tilt on the hero image
  const handleMove = (e: React.MouseEvent) => {
    const el = tiltRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const ry = ((e.clientX - r.left) / r.width - 0.5) * 10;
    const rx = ((e.clientY - r.top) / r.height - 0.5) * -10;
    el.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg)`;
  };
  const handleLeave = () => {
    if (tiltRef.current)
      tiltRef.current.style.transform = "perspective(900px) rotateX(0deg) rotateY(0deg)";
  };

  return (
    <div>
      <NavBar />
      <style>{STYLES}</style>

      <div className="bp">
        <div className="bp-wrap">
          {/* HERO */}
          <div className="bp-hero">
            <div className="bp-text">
              <div className="bp-chip">Our Blog</div>
              <h1>{title}</h1>
              <div className="bp-line"><i /><i /></div>
              <div className="bp-meta">
                <span className="bp-pill">Published on {date}</span>
                <span className="bp-pill">{readTime}</span>
              </div>
              {tags.length > 0 && (
                <div className="bp-tags">
                  {tags.map((t) => (
                    <span className="bp-tag" key={t}>{t}</span>
                  ))}
                </div>
              )}
            </div>

            <div className="bp-vis" onMouseMove={handleMove} onMouseLeave={handleLeave}>
              <div className="bp-ring" />
              <div className="bp-tilt" ref={tiltRef}>
                <img src={heroImg} alt={heroAlt} />
              </div>
            </div>
          </div>

          {/* ARTICLE */}
          <article className="bp-article">
            {intro && (
              <Reveal>
                <div className="bp-intro"><p>{intro}</p></div>
              </Reveal>
            )}

            {children}

            <div className="bp-foot">
              <Link to={backTo} className="bp-btn back">
                <span>←</span> Back to Blog
              </Link>
              {disclaimer && <small>{disclaimer}</small>}
            </div>
          </article>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BlogPostLayout;